import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import moment from 'moment-timezone';

import './Dashboard.scss';
import Strings from '../../../../Strings';
import { DATE_MONTH_DAY_YEAR, DATE_TIME_SPACE_12_UPPERCASE } from '../../../../constants';
import { datesText, getProgramDisplayName } from '../../../../utils/cmsPrograms';
import { getVitalsLabelsFromEnrollment } from '../../../../utils/enrollmentFields';
import { shippingActions } from '../../../Shipping/actions';
import { shipmentStatusEnum } from '../../../../utils/shipmentConstants';
import ConsentInfo from '../../../../components/Consent/ConsentInfo';
import { showsEnrollmentConsent } from '../../../../utils/consentTemplates';

function PatientInfo(props) {
  const { data } = props;
  const { dob, gender, language } = data || {};
  
  return (
    <div className="vertical-layout-widet col">
      <div className="vertical-layout-widet">
        <div className="vertical-layout-widet info-label">{Strings.dob}</div>
        <div className="vertical-layout-widet info-value" data-testid="info-dob">
          {dob ? moment.tz(dob, 'UTC').format(DATE_MONTH_DAY_YEAR) : '-'}
        </div>
      </div>
      <div className="vertical-layout-widet">
        <div className="vertical-layout-widet info-label">{Strings.gender}</div>
        <div className="vertical-layout-widet info-value" data-testid="info-gender">{Strings.genders[gender?.toLowerCase()] || gender || '-'}</div>
      </div>
      <div className="vertical-layout-widet">
        <div className="vertical-layout-widet info-label">{Strings.language}</div>
        <div className="vertical-layout-widet info-value" data-testid="info-language">{Strings.languagesEnum[language] || language || '-'}</div>
      </div>
    </div>
  );
}
PatientInfo.propTypes = {
  onClick: PropTypes.func,
  data: PropTypes.shape({
    dob: PropTypes.any,
    gender: PropTypes.string,
    language: PropTypes.string,
  }),
  refreshTimestamp: PropTypes.any,
};

function DevicesAndMeds(props) {
  return (
    <div className="vertical-layout-widet row">
      <div
        className="vertical-layout-widet row clickable"
        onClick={() => props.onClick(`/cap-patients/${props.patientId}/devices`)}
      >
        <div className="vertical-layout-widet value">{props.data?.devices}</div>
        <div className="vertical-layout-widet descr">{Strings.devices}</div>
        <div className="nav-icon" />
      </div>
      <div className="vertical-layout-widet col-sep"></div>
      <div
        className="vertical-layout-widet row clickable"
        onClick={() => props.onClick(`/cap-patients/${props.patientId}/medications`)}
      >
        <div className="vertical-layout-widet value">{props.data?.medications}</div>
        <div className="vertical-layout-widet descr">{Strings.medications}</div>
        <div className="nav-icon" />
      </div>
    </div>
    /* TODO: Lack of defintion what new mean */
    /* <div className="red-badge">+1 {Strings.new}</div> */
  );
}
DevicesAndMeds.propTypes = {
  onClick: PropTypes.func,
  data: PropTypes.shape({
    devices: PropTypes.number,
    medications: PropTypes.number,
  }),
  refreshTimestamp: PropTypes.any,
};

function Documents(props) {
  return (
    <div
      className="vertical-layout-widet row clickable"
      onClick={() => props.onClick(`/cap-patients/${props.patientId}/documents`)}
    >
      <div className="vertical-layout-widet value">{props.data?.documents}</div>
      <div className="vertical-layout-widet descr">{Strings.documents}</div>
      <div className="nav-icon" />
    </div>
  );
}
DevicesAndMeds.propTypes = {
  onClick: PropTypes.func,
  data: PropTypes.shape({
    documents: PropTypes.number,
  }),
  refreshTimestamp: PropTypes.any,
};

const mapDispatchToProps = dispatch => ({
  onClick: path => dispatch(push(path)),
  getShipmentDetails: id => dispatch(shippingActions.getShipmentDetails(id)),
});

export const getEnrollmentInfo = (enrollment, { onRecordConsent, consentDetailsInPopover } = {}) => {
  const array = [];
  if (enrollment?.program) {
    array.push({
      name: Strings.program,
      value: getProgramDisplayName(enrollment.program, enrollment.subprogram),
      "data-testid": "info-program",
    });
  }
  if (showsEnrollmentConsent(enrollment)) {
    array.push({
      name: Strings.consentChecklist.label,
      value: <ConsentInfo enrollment={enrollment} onRecordConsent={onRecordConsent} detailsInPopover={consentDetailsInPopover} />,
      "data-testid": "info-consent",
      className: "consent-cell",
    });
  }
  if (enrollment?.enrollmentStart || enrollment?.enrollmentEnd) {
    array.push({
      name: Strings.activationStartEnd,
      value: datesText(enrollment),
      "data-testid": "info-enrollment-start-end",
    });
  }
  if (enrollment?.careProviderOrg?.name) {
    array.push({
      name: Strings.capPatient.careProvider,
      value: enrollment.careProviderOrg.name,
      "data-testid": "info-careprovider-name",
    });
  }
  if (enrollment?.allowedVitals && enrollment.program === 'RPM') {
    array.push({
      name: Strings.vitals,
      value: getVitalsLabelsFromEnrollment(enrollment, { withIncluded: true })?.join(", "),
      tooltip: getVitalsLabelsFromEnrollment(enrollment, { withIncluded: true })?.join(", "),
      "data-testid": "info-vitals",
    });
  }
  if (enrollment?.conditionTitle) {
    array.push({
      name: Strings.condition,
      value: enrollment.conditionTitle,
      tooltip: enrollment.conditionTitle,
      "data-testid": "info-condition-title",
    });
  }
  if (enrollment?.medications?.length > 0) {
    array.push({
      name: Strings.medications,
      value: enrollment.medications.map(m => m.name).join(", "),
      tooltip: enrollment.medications.map(m => m.name).join(", "),
      "data-testid": "info-medications",
    });
  }
  return array.filter(t => t);
};
function EnrollmentInfo({enrollment, editEnrollment}) {
  if (!enrollment) {
    return null;
  }

  const enrollmentInfo = getEnrollmentInfo(enrollment, { consentDetailsInPopover: true });
  const canEnrollmentBeEdited = 
   (enrollment?.enrollmentEnd && moment().isBefore(enrollment.enrollmentEnd) || !enrollment?.enrollmentEnd)
    && enrollment.program === 'RPM' && editEnrollment;
  return (
    <div className="vertical-layout-widet col" style={{position: 'relative'}}>
      {canEnrollmentBeEdited && (
        <div
          onClick={() => editEnrollment(enrollment)}
          className="edit-button"
          data-testid="edit-enrollment-button"
        >
          <div className="icon" />
        </div>
      )}
      {enrollmentInfo.map(info => (
        <div className={`vertical-layout-widet${info.className ? ` ${info.className}` : ''}`} key={info.name}>
          <div className="vertical-layout-widet info-label">{info.name}</div>
          <div
            className="vertical-layout-widet info-value"
            data-tooltip-id={info.tooltip ? "tooltip" : undefined}
            data-tooltip-content={info.tooltip}
            data-testid={info["data-testid"]}
          >
            {info.value}
          </div>
        </div>
      ))}
    </div>
  );
}
EnrollmentInfo.propTypes = {
  enrollment: PropTypes.object,
  editEnrollment: PropTypes.func,
};

function ShippingInfo({enrollment, getShipmentDetails}) {
  const [shipment, setShipment] = React.useState(enrollment?.shipment);
  React.useEffect(() => {
    if (enrollment?.shipment?.shipment_uuid) {
      getShipmentDetails(enrollment.shipment.shipment_uuid).then(resp => {
        if (resp?.response) {
          setShipment(resp.response);
        }
      });
    }
  }, [enrollment, getShipmentDetails]);

  const deliveredDate = React.useMemo(() => {
    const deliveredHistoryEntry = shipment?.status_history?.find(entry => entry.status === 'delivered');
    if (deliveredHistoryEntry?.created_at) {
      return moment(deliveredHistoryEntry.created_at).format(DATE_TIME_SPACE_12_UPPERCASE);
    }
    return null;
  }, [shipment]);

  return (
    <div className="vertical-layout-widet col">
      <div className="vertical-layout-widet">
        <div className="vertical-layout-widet info-label">{Strings.shipping.shippingStatus}</div>
        <div className="vertical-layout-widet info-value">
          {Strings.shipping.shipmentStatuses[shipment?.status]}
        </div>
      </div>
      {shipment?.tracking_number && (
        <div className="vertical-layout-widet">
          <div className="vertical-layout-widet info-label">{Strings.shipping.trackingNumber}</div>
          <div className="vertical-layout-widet info-value">
            {shipment?.tracking_number}
          </div>
        </div>
      )}
      {shipment?.provider_order_no && (
        <div className="vertical-layout-widet">
          <div className="vertical-layout-widet info-label">{Strings.shipping.providerOrderNumber}</div>
          <div className="vertical-layout-widet info-value">
            {shipment?.provider_order_no}
          </div>
        </div>
      )}
      {deliveredDate && (
        <div className="vertical-layout-widet">
          <div className="vertical-layout-widet info-label">{Strings.shipping.deliveryDate}</div>
          <div className="vertical-layout-widet info-value">
            {deliveredDate}
          </div>
        </div>
      )}
      {shipment?.status === shipmentStatusEnum.in_transit && shipment?.eta && (
        <div className="vertical-layout-widet">
          <div className="vertical-layout-widet info-label">{Strings.shipping.eta}</div>
          <div className="vertical-layout-widet info-value">
            {shipment?.eta ? moment(shipment.eta).format(DATE_MONTH_DAY_YEAR) : '-'}
          </div>
        </div>
      )}
    </div>
  );
}
ShippingInfo.propTypes = {
  enrollment: PropTypes.object,
  getShipmentDetails: PropTypes.func,
};

export const getProgramWidgetDataLength = enrollment => {
  let count = 0;
  if (enrollment?.program) count++;
  if (showsEnrollmentConsent(enrollment)) count++;
  if (enrollment?.enrollmentStart || enrollment?.enrollmentEnd) count++;
  if (enrollment?.careProviderOrg?.name) count++;
  if (enrollment?.allowedVitals && enrollment.program === 'RPM') count++;
  if (enrollment?.conditionTitle) count++;
  if (enrollment?.medications?.length > 0) count++;
  return count;
}

export const getShipmentWidgetDataLength = enrollment => {
  let count = 1;
  if (enrollment?.shipment?.tracking_number) count++;
  if (enrollment?.shipment?.provider_order_no) count++;
  if (enrollment?.shipment?.status === shipmentStatusEnum.delivered || enrollment?.shipment?.status === shipmentStatusEnum.in_transit) count++;
  return count;
}

export const PatientInfoWidget = connect(null, mapDispatchToProps)(PatientInfo);
export const DevicesAndMedsWidget = connect(null, mapDispatchToProps)(DevicesAndMeds);
export const DocumentsWidget = connect(null, mapDispatchToProps)(Documents);
export const EnrollmentInfoWidget = EnrollmentInfo;
export const ShippingInfoWidget = connect(null, mapDispatchToProps)(ShippingInfo);
